import { Box3, Group, Vector3, use } from '@anov/3d-core'
import type { Mesh, Object3D, SceneControl } from '@anov/3d-core'
import _ from 'lodash'
import ObjectLayer from '../Base/ObjectLayer'
import { lookAtOnYAxis } from './tools'
import type { IPoi, ModelOption, Option, ParticleOption, TextOption } from './types'
import { StrategyManage as LinkStrategy } from './link'
import { StrategyManage as ParticleStrategy } from './particle'
import { StrategyManage as TitleStrategy } from './title'
import InfoPanel from './panel'

const DEFAULT_OPTION = {
  style: 'poi_border_01',
  facingCamera: true,
  autoScale: true,
  text: {
    visible: true,
    value: '标题文字',
    fontSize: 16,
    color: '#ffffff',
    fontWeight: 'normal',
    offset: [0, 0],
    bgUrl: '',
    uri: '',
  },
  model: {
    visible: true,
    uri: '',
    color: '#2C8AFF',
  },
  particle: {
    visible: true,
    uri: '',
    color: '#FFFFFF',
  },
}

const STYLE_DATA = {
  poi_border_01: ['border', 'pyramid', 'circle'],
  poi_ellipse_01: ['ellipse', 'cylinder', 'circle'],
}

const MAX_DISTANCE = 1000

class Poi extends ObjectLayer implements IPoi {
  private _mesh!: Group
  private _titleMesh: Mesh & any
  private _linkMesh: Mesh | Group & any
  private _particleMesh: Mesh & any
  private _panel?: InfoPanel
  private _disposeCallbacks: Function[] = []

  private _style: string
  private _autoScale: boolean
  private _facingCamera: boolean
  private _text: TextOption
  private _model: ModelOption
  private _particle: ParticleOption
  private _content: TextOption
  type = 'Group'

  constructor(option: Option, sceneControl: SceneControl) {
    super(sceneControl)

    const param = _.mergeWith(
      _.cloneDeep(DEFAULT_OPTION),
      option,
      this.arrayMergeCustomizer,
    )
    const { style, text, model, particle, autoScale, facingCamera, content } = param
    this._style = style
    this._text = text
    this._model = model
    this._particle = particle
    this._autoScale = autoScale
    this._facingCamera = facingCamera
    this._content = content
    switch (this.style) {
      case 'poi_border_01':
      case 'poi_ellipse_01':
      case 'poi_custom':
        this.init()
        break
      case 'poi_card_01':
        this.initCard(param)
        break
    }
  }

  get content(): TextOption {
    return this._content
  }

  get mesh() {
    return this._mesh
  }

  get style(): string {
    return this._style
  }

  get autoScale(): boolean {
    return this._autoScale
  }

  set autoScale(newValue: boolean) {
    this._autoScale = newValue
  }

  get facingCamera(): boolean {
    return this._facingCamera
  }

  set facingCamera(newValue: boolean) {
    this._facingCamera = newValue
  }

  get text(): TextOption {
    return this._text
  }

  get model(): ModelOption {
    return this._model
  }

  get particle(): ParticleOption {
    return this._particle
  }

  destroy() {
    this._disposeCallbacks.forEach(fn => fn())
    this._disposeCallbacks = []
    this.remove(this._mesh)
  }

  private async init() {
    const group = new Group()
    const [titleStyle, linkStyle, particleStyle] = STYLE_DATA[this.style]

    const strategies = {
      _titleMesh: { strategy: TitleStrategy[titleStyle], config: this.text, condition: this.text?.visible !== false },
      _linkMesh: { strategy: LinkStrategy[linkStyle], config: this.model, condition: !this.model || this.model.visible !== false },
      _particleMesh: { strategy: ParticleStrategy[particleStyle], config: this.particle, condition: !this.particle || this.particle.visible !== false },
    }

    const loadTasks = Object.entries(strategies)
      .filter(([, { condition }]) => condition)
      .map(async ([key, { strategy, config }]) => {
        if (!strategy)
          return
        const ClassRef = await strategy()
        this[key] = new ClassRef({ ...config }, this.sceneControl)
        await this[key].ready
        group.add(this[key])
      })

    await Promise.all(loadTasks)

    this._mesh = group
    this.calculateLayout()
    this.setupBoundingBox(group)
    group.updateWorldMatrix(true, true)
    this._linkMesh?.setupAnimation()
    this.add(group)

    this._text = this.createProxy(this._text, this._titleMesh)
    this._model = this.createProxy(this._model, this._linkMesh)
    this._particle = this.createProxy(this._particle, this._particleMesh)

    this.addEventListeners()
  }

  private initCard(param: any) {
    const group = new Group()
    this._mesh = group
    const panel = new InfoPanel(param, this.sceneControl)
    this._panel = panel
    group.add(panel)
    this._autoScale = false
    this.add(group)
    this.addEventListeners()
  }

  private calculateLayout() {
    const titleSize = this._titleMesh ? this.getBoxSize(this._titleMesh) : { height: 0 }
    const linkSize = this._linkMesh ? this.getBoxSize(this._linkMesh) : { height: 0 }

    let baseY = -titleSize.height / 2
    const linkHeight = linkSize.height
    const linkVSpace = this._linkMesh?.vspace?.[0] || 0

    baseY -= linkVSpace
    this._linkMesh?.position.setY(baseY - linkHeight / 2)
    this._particleMesh?.position.setY(baseY - linkHeight)
  }

  private getBoxSize(mesh: any) {
    const { max, min } = new Box3().setFromObject(mesh)
    return { width: max.x - min.x, height: max.y - min.y }
  }

  private setupBoundingBox(mesh: Object3D) {
    const box = new Box3().setFromObject(mesh)
    const size = new Vector3()
    const center = new Vector3()
    box.getSize(size)
    box.getCenter(center)

    const bottomCenter = new Vector3(center.x, box.min.y, center.z)
    mesh.children.forEach((mesh) => {
      mesh.position.sub(bottomCenter)
    })
  }

  private updateViewDirection() {
    const { camera } = use.useScene(this.sceneControl)

    lookAtOnYAxis(this._mesh.parent, camera.position)
  }

  private updateScale() {
    const { camera } = use.useScene(this.sceneControl)
    const distance = camera?.position.distanceTo(this._mesh.position)
    if (distance >= MAX_DISTANCE)
      return

    const scale = distance / 20 || 1
    this._mesh.scale.set(scale, scale, scale)
  }

  private addEventListeners() {
    const updateTransform = () => {
      const { camera } = use.useScene(this.sceneControl)
      this._facingCamera !== false && this.updateViewDirection()
      this._autoScale === true && this.updateScale()
      if (this._panel && camera) {
        this._panel.updateFacing(camera)
        this._panel.updateDynamicScale(camera)
      }
    }

    const scene = use.useScene(this.sceneControl)
    const callback = () => updateTransform()
    updateTransform()
    scene.orbitControls?.addEventListener('change', callback)
    this._disposeCallbacks.push(() => {
      scene.orbitControls?.removeEventListener('change', callback)
    })

    const cancelFrame = use.useframe(this.sceneControl, () => {
      updateTransform()
    })
    this._disposeCallbacks.push(() => cancelFrame())
  }
}

export { Poi }
